import Poll from "@/Components/Poll/Poll";
import { mergeAttributes, Node } from "@tiptap/core";
import {
  NodeViewWrapper,
  ReactNodeViewProps,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { useTranslation } from "react-i18next";
import RteIcon from "./RteIcon";
import { cn } from "@/shared/utils/cn";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    poll: {
      insertPoll: (pollId: number) => ReturnType;
    };
  }
}

export interface PollExtensionOptions {
  // Read only polls without voting, for previews
  preview: boolean;
}

const PollNodeView = ({
  node,
  editor,
  extension,
  selected,
  deleteNode,
}: ReactNodeViewProps) => {
  const { t } = useTranslation("", { keyPrefix: "systemMessages.editor" });
  const pollId = Number(node.attrs.pollId);
  const { preview } = extension.options as PollExtensionOptions;

  if (!editor.isEditable) {
    return (
      <NodeViewWrapper className="my-2">
        <Poll pollId={pollId} preview={preview} />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className={cn(
        "group relative my-2 rounded-[1.8rem] outline-offset-4 transition-[outline-color]",
        selected ? "outline-2 outline-primary" : "outline-2 outline-transparent"
      )}
    >
      <div className="absolute right-4 top-4 z-1 flex gap-1 rounded-lg border border-border bg-white p-1 opacity-100 shadow-sm transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
        <span
          data-drag-handle
          draggable="true"
          contentEditable={false}
          title={t("dragPoll")}
          className="flex h-[3rem] w-[3rem] cursor-grab items-center justify-center rounded-md text-text-tertiary hover:bg-light/40 active:cursor-grabbing"
        >
          <RteIcon name="grip" />
        </span>
        <button
          type="button"
          title={t("removePoll")}
          aria-label={t("removePoll")}
          onClick={deleteNode}
          className="flex h-[3rem] w-[3rem] cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-error hover:bg-error/10"
        >
          <RteIcon name="trash" />
        </button>
      </div>
      <Poll pollId={pollId} preview />
    </NodeViewWrapper>
  );
};

/**
 * Embeds a poll in rich text, stored as <div data-type="poll" data-poll-id="1">.
 * Only the id is saved, the poll itself is always fetched fresh.
 */
const PollExtension = Node.create<PollExtensionOptions>({
  name: "poll",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { preview: false };
  },

  addAttributes() {
    return {
      pollId: {
        default: null,
        parseHTML: (element) =>
          Number(element.getAttribute("data-poll-id")) || null,
        renderHTML: (attributes) => ({ "data-poll-id": attributes.pollId }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-type='poll']" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-type": "poll" })];
  },

  addCommands() {
    return {
      insertPoll:
        (pollId) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { pollId } }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(PollNodeView, {
      // Read only: the poll handles its own clicks. Editing: the poll's
      // controls work, a click elsewhere on the card selects it for the editor
      stopEvent: ({ event }) => {
        if (!this.editor.isEditable) return true;
        const target = event.target as HTMLElement | null;
        return Boolean(target?.closest?.("button, input, a"));
      },
    });
  },
});

export default PollExtension;
